import { useState, useRef, useEffect } from 'react';
import { Filter, ChevronDown, Check } from 'lucide-react';
import { useInfrastructureCache } from '../../../hooks/useInfrastructureCache';
import React from 'react';

const TopLayer = ({ onFilterChange, onDateRangeChange }) => {
  const [selectedFilter, setSelectedFilter] = useState('Product'); 
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isFromDateOpen, setIsFromDateOpen] = useState(false);
  const [isToDateOpen, setIsToDateOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const filterRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const { data: infrastructure } = useInfrastructureCache();

  const availableTagKeys = React.useMemo(() => {
    if (!infrastructure?.tags) return [];
    const keys = [...new Set(infrastructure.tags.map(tag => tag.key))];
    return keys;
  }, [infrastructure?.tags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
      if (fromDateRef.current && !fromDateRef.current.contains(event.target)) {
        setIsFromDateOpen(false);
      }
      if (toDateRef.current && !toDateRef.current.contains(event.target)) {
        setIsToDateOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterSelect = (filterType) => {
    setSelectedFilter(filterType);
    setIsFilterOpen(false);
    
    if (onFilterChange) {
      onFilterChange({ type: filterType });
    }
  };

  const handleDateSelect = (type, date) => {
    if (type === 'from') {
      setFromDate(date);
      setIsFromDateOpen(false);
      if (onDateRangeChange) {
        onDateRangeChange(date, toDate);
      }
    } else {
      setToDate(date);
      setIsToDateOpen(false);
      if (onDateRangeChange) {
        onDateRangeChange(fromDate, date);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Select date';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getButtonText = () => {
    if (selectedFilter === 'all') return 'All VMs';
    return `By ${selectedFilter}`;
  };

  const CalendarPicker = ({ onSelect, currentDate, onClose, position = 'left' }) => {
    const getDaysInMonth = (year, month) => {
      return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
      return new Date(year, month, 1).getDay();
    };

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    const changeMonth = (delta) => {
      setCurrentMonth(new Date(year, month + delta, 1));
    };

    const isSelected = (date) => {
      if (!currentDate) return false;
      return date.toDateString() === currentDate.toDateString();
    };

    const isToday = (date) => {
      return date.toDateString() === today.toDateString();
    };

    return (
      <div className={`absolute top-full mt-2 ${position === 'left' ? 'left-0' : 'right-0'} bg-card-bg rounded-2xl shadow-xl border border-border-color z-50 p-4 w-72 sm:w-80`}>
        <div className="flex justify-between items-center mb-4">
          <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-bg-main rounded-lg transition-colors">
            ←
          </button>
          <span className="font-semibold text-primary-dark text-sm sm:text-base">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={() => changeMonth(1)} className="p-1 hover:bg-bg-main rounded-lg transition-colors">
            →
          </button>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="text-center text-xs text-primary-dark/60 font-medium py-1">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, idx) => (
            <div key={idx} className="aspect-square">
              {date ? (
                <button
                  onClick={() => {
                    onSelect(date);
                    onClose();
                  }}
                  className={`w-full h-full rounded-lg text-xs sm:text-sm transition-all ${
                    isSelected(date)
                      ? 'bg-primary-cyan text-white'
                      : isToday(date)
                      ? 'bg-primary-dark text-white'
                      : 'hover:bg-bg-main text-primary-dark'
                  }`}
                >
                  {date.getDate()}
                </button>
              ) : (
                <div className="w-full h-full" />
              )}
            </div>
          ))}
        </div>
        
        <button
          onClick={() => {
            onSelect(today);
            onClose();
          }}
          className="mt-4 w-full py-2 text-sm text-primary-cyan hover:bg-bg-main rounded-lg transition-colors"
        >
          Today
        </button>
      </div>
    );
  };

  return (
    <div className="bg-card-bg rounded-xl border border-border-color p-3 sm:p-4 mb-6 shadow-sm">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
        <div className="relative w-full sm:w-auto" ref={filterRef}>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center justify-between gap-2 px-4 py-2 bg-primary-dark text-white rounded-lg hover:opacity-90 transition-all font-medium w-full sm:w-auto min-w-[160px] sm:min-w-[180px]"
          >
            <span className="flex items-center gap-2">
              <Filter size={18} />
              <span className="text-sm sm:text-base">{getButtonText()}</span>
            </span>
            <ChevronDown size={16} className={`transition-transform ${isFilterOpen ? 'rotate-180' : ''}`} />
          </button>

          {isFilterOpen && (
            <div className="absolute top-full mt-2 left-0 bg-card-bg rounded-xl shadow-lg border border-border-color z-50 min-w-[200px] w-full sm:w-auto overflow-hidden">
              <button
                onClick={() => handleFilterSelect('all')}
                className={`w-full text-left px-4 py-2 hover:bg-bg-main transition-colors flex items-center justify-between ${
                  selectedFilter === 'all' ? 'bg-bg-main text-primary-cyan' : 'text-primary-dark'
                }`}
              >
                All VMs
                {selectedFilter === 'all' && <Check size={16} />}
              </button>
              
              <div className="border-t border-border-color my-1" />
              
              {availableTagKeys.map(tagKey => (
                <button
                  key={tagKey}
                  onClick={() => handleFilterSelect(tagKey)}
                  className={`w-full text-left px-4 py-2 hover:bg-bg-main transition-colors flex items-center justify-between ${
                    selectedFilter === tagKey ? 'bg-bg-main text-primary-cyan' : 'text-primary-dark'
                  }`}
                >
                  By {tagKey}
                  {selectedFilter === tagKey && <Check size={16} />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopLayer;